import gsap from "gsap";
import { Observer } from "gsap/Observer";

gsap.registerPlugin(Observer);

export default function initReviews() {
  document.querySelectorAll('.reviews-block').forEach((block) => {

    const contentTrack = block.querySelector(".slider-content");
    const imageTrack = block.querySelector(".slider-images");
    const pagination = block.querySelector(".pagination");

    // The image track is optional: when no review has an image the template
    // omits that column entirely, and the slider still has to run on text alone.
    if (!contentTrack || !pagination) return;

    const contentSlides = Array.from(contentTrack.querySelectorAll(".slide"));
    const imageSlides = imageTrack ? Array.from(imageTrack.querySelectorAll(".slide")) : [];
    const total = Math.max(contentSlides.length, imageSlides.length);
    if (!total) return;

    // --- Accessibility: make the block a labelled region ---
    block.setAttribute("role", "region");
    block.setAttribute("aria-roledescription", "carousel");
    block.setAttribute("aria-label", "Customer reviews");

    // Make content track announce changes to screen readers
    contentTrack.setAttribute("aria-live", "polite");
    contentTrack.setAttribute("aria-atomic", "true");

    // Label each content slide
    contentSlides.forEach((slide, i) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      slide.setAttribute("aria-label", `Review ${i + 1} of ${contentSlides.length}`);
    });

    // --- Build pagination bullets (one per slide) ---
    pagination.setAttribute("role", "tablist");
    pagination.setAttribute("aria-label", "Review slides");
    pagination.innerHTML = "";
    const bullets = [];
    for (let i = 0; i < total; i++) {
      const b = document.createElement("button");
      b.className = "pagination__item";
      b.setAttribute("type", "button");
      b.setAttribute("role", "tab");
      b.setAttribute("aria-label", `Go to review ${i + 1}`);
      b.setAttribute("aria-selected", i === 0 ? "true" : "false");
      b.setAttribute("tabindex", i === 0 ? "0" : "-1");
      b.addEventListener("click", () => goTo(i));
      pagination.appendChild(b);
      bullets.push(b);
    }

    // --- Base states (no FOUC) ---
    gsap.set(contentSlides, { autoAlpha: 0, yPercent: 10 });
    gsap.set(imageSlides, { autoAlpha: 0, scale: 1.02 });

    let index = 0;
    let isAnimating = false;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Show initial
    gsap.set(contentSlides[0 % contentSlides.length], { autoAlpha: 1, yPercent: 0 });
    if (imageSlides.length) gsap.set(imageSlides[0], { autoAlpha: 1, scale: 1 });
    bullets[0].classList.add("is-active");

    // --- Helpers ---
    const mod = (n, m) => ((n % m) + m) % m;

    function setBullet(i) {
      bullets.forEach((b, bi) => {
        const active = bi === i;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-selected", active ? "true" : "false");
        b.setAttribute("tabindex", active ? "0" : "-1");
      });
    }

    // Animation between slides i -> j (sync text & image)
    function goTo(newIndex, opts = { immediate: false }) {
      if (isAnimating || newIndex === index) return;
      isAnimating = true;

      const fromC = contentSlides[mod(index, contentSlides.length)];
      const toC = contentSlides[mod(newIndex, contentSlides.length)];
      // mod(n, 0) is NaN, so guard the lookup rather than indexing an empty track.
      const fromI = imageSlides.length ? imageSlides[mod(index, imageSlides.length)] : null;
      const toI = imageSlides.length ? imageSlides[mod(newIndex, imageSlides.length)] : null;

      const durText = prefersReduced ? 0.3 : (opts.immediate ? 0 : 0.7);
      const durImage = prefersReduced ? 0.3 : (opts.immediate ? 0 : 0.6);

      const targetIndex = mod(newIndex, total);

      setBullet(targetIndex);

      // Prepare incoming
      gsap.set(toC, { autoAlpha: 0, yPercent: 20 });
      if (toI) gsap.set(toI, { autoAlpha: 0, scale: 1.02 });

      const tl = gsap.timeline({
        defaults: { overwrite: true },
        onComplete: () => {
          index = targetIndex;
          isAnimating = false;
        }
      });

      // Image crossfade (skipped entirely on text-only sliders)
      if (fromI) tl.to(fromI, { autoAlpha: 0, scale: 1.02, duration: durImage, ease: "power2.out" }, 0);
      if (toI) tl.to(toI, { autoAlpha: 1, scale: 1.0, duration: durImage, ease: "power2.out" }, 0);

      // Text out/in with a touch of bounce on enter
      tl.to(fromC, { autoAlpha: 0, yPercent: -10, duration: durText * 0.6, ease: "power2.in" }, 0);
      tl.to(toC, { autoAlpha: 1, yPercent: 0, duration: durText, ease: "back.out(1.7)" }, 0.6);

      // Reset autoplay timer
      resetAutoplay();
    }

    function next() {
      goTo(index + 1);
    }
    function prev() {
      goTo(index - 1);
    }

    // --- Autoplay ---
    const AUTO_DELAY = 5; // seconds
    let autoCall = null;
    let manualPaused = false;

    function resetAutoplay() {
      if (autoCall) autoCall.kill();
      if (manualPaused) return;
      autoCall = gsap.delayedCall(AUTO_DELAY, next);
    }

    function pauseAutoplay() {
      if (autoCall) autoCall.kill();
    }

    // Manual toggle
    const toggleBtn = block.querySelector('.reviews-block__toggle');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        manualPaused = !manualPaused;
        toggleBtn.classList.toggle('is-paused', manualPaused);
        toggleBtn.setAttribute('aria-label', manualPaused ? 'Play slider' : 'Pause slider');

        if (manualPaused) {
          pauseAutoplay();
        } else {
          resetAutoplay();
        }
      });
    }

    // Pause when tab hidden
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        pauseAutoplay();
      } else {
        resetAutoplay();
      }
    });

    // Pause autoplay while the slider has keyboard focus
    block.addEventListener("focusin", pauseAutoplay);
    block.addEventListener("focusout", resetAutoplay);

    // Start
    resetAutoplay();

    // --- Keyboard navigation ---
    block.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        next();
        bullets[mod(index + 1, total)]?.focus();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        prev();
        bullets[mod(index - 1, total)]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
        bullets[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(total - 1);
        bullets[total - 1]?.focus();
      }
    });

    // --- Swipe on mobile (horizontal only) ---
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    if (isCoarse) {
      Observer.create({
        target: block,
        type: "touch,pointer",
        tolerance: 10,
        preventDefault: false,
        allowClicks: true,
        onLeft(self) { if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) next(); },
        onRight(self) { if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) prev(); },
        onChange: () => pauseAutoplay(),
        onRelease: () => resetAutoplay(),
      });
    }

  });

}
